/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
    QAvatarProps,
    QBtnProps,
    QForm,
    QIconProps,
    QInput,
    QItemProps,
    QTooltipProps
} from "quasar";
import type { ComponentPublicInstance } from "vue";

export const isNullish = <T>(
    argument: T | undefined | null
): argument is undefined | null => argument === null || argument === undefined;
export const isNonNullish = <T>(
    argument: T | undefined | null
): argument is NonNullable<T> => !isNullish(argument);
export const isRecord = (arg: unknown): arg is Record<PropertyKey, unknown> =>
    typeof arg === "object" && arg !== null && !Array.isArray(arg);

export function attachToWindow<T extends string, V>(key: T, value: V) {
    (window as unknown as Record<T, V>)[key] = value;
}
function dateOrDefault(val: unknown): Date | undefined;
function dateOrDefault<D>(val: unknown, def: D): Date | D;
function dateOrDefault<D>(val: unknown, def?: D) {
    if (val instanceof Date) {
        return val;
    }
    const parsed = Date.parse(val as string);
    return isNaN(parsed) ? def : new Date(parsed);
}
export function typeOrDefault<T extends PrimitiveOrConstructor>(
    val: unknown,
    type: T
): GuardedType<T> | undefined;
export function typeOrDefault<T extends PrimitiveOrConstructor, D>(
    val: unknown,
    type: T,
    def: D
): GuardedType<T> | D;
export function typeOrDefault<T extends PrimitiveOrConstructor, D>(
    val: unknown,
    type: T,
    def?: D
) {
    if (type === "date" || type === "Date") {
        return dateOrDefault(val, def);
    }
    return genericTypeGuard(val, type) ? val : def;
}
export function isNonNullableObject(obj: unknown): obj is NonNullable<object> {
    return typeof obj === "object" && obj !== null;
}
export function hasKey<O, K extends PropertyKey>(
    obj: O,
    key: K
): obj is O & Record<K, unknown> {
    return isNonNullableObject(obj) && key in obj;
}
/** do not use this with union types in place of F */
export function propGuard<
    O,
    F extends PropertyKey,
    T extends PrimitiveOrConstructor
>(obj: O, field: F, type: T): obj is O & Record<F, GuardedType<T>> {
    return hasKey(obj, field) && genericTypeGuard(obj[field], type);
}
export type ActionButtonProps = QBtnProps & {
    dropdown?: (QItemProps & {
        label?: string;
        icon?: QIconProps;
        avatar?: QAvatarProps;
        tooltip?: QTooltipProps & { content: string };
    })[];
    tooltip?: QTooltipProps & { content: string };
};
export type OptionalKeys<T> = {
    [K in keyof T]-?: {} extends Pick<T, K>
        ? K
        : undefined extends T[K]
          ? K
          : never;
}[keyof T];
export type Option<T> = T | undefined;
export type InputRef = QInput | null;
export type FormRef = QForm | null;
export type SelectRef = HTMLSelectElement | null;
export type ComponentRef<T extends abstract new (...args: any) => any> =
    InstanceType<T> | null;
export type PublicComponentRef = ComponentPublicInstance | null;

//helpers for generic type guards
interface typeMap {
    // for mapping from strings to types
    string: string;
    number: number;
    boolean: boolean;
    bigint: bigint;
    object: object;
    array: Array<unknown>;
    Array: Array<unknown>;
    date: Date;
    Date: Date;
    "unknown[]": Array<unknown>;
    "Array<any>": Array<any>;
    "any[]": Array<any>;
    "Array<string>": Array<string>;
    "string[]": Array<string>;
    "Array<number>": Array<number>;
    "number[]": Array<number>;
    "Array<bigint>": Array<bigint>;
    "bigint[]": Array<bigint>;
    "Array<boolean>": Array<boolean>;
    "boolean[]": Array<boolean>;
    "Array<object>": Array<object>;
    "object[]": Array<object>;
    "Array<NonNullable<object>>": Array<NonNullable<object>>;
    "NonNullable<object>[]": NonNullable<object>[];
}
type PrimitiveOrConstructor = (new (...args: any[]) => any) | keyof typeMap;
// infer the guarded type from a specific case of PrimitiveOrConstructor
type GuardedType<T extends PrimitiveOrConstructor> = T extends new (
    ...args: any[]
) => infer U
    ? U
    : T extends keyof typeMap
      ? typeMap[T]
      : never;

export function genericTypeGuard<T extends PrimitiveOrConstructor>(
    value: unknown,
    type: T
): value is GuardedType<T> {
    const localPrimitiveOrConstructor: PrimitiveOrConstructor = type;
    if (typeof localPrimitiveOrConstructor === "string") {
        if (
            localPrimitiveOrConstructor === "date" ||
            localPrimitiveOrConstructor === "Date"
        ) {
            return value instanceof Date;
        } else if (
            localPrimitiveOrConstructor === "array" ||
            localPrimitiveOrConstructor === "Array" ||
            localPrimitiveOrConstructor === "unknown[]"
        ) {
            return Array.isArray(value);
        } else if (
            localPrimitiveOrConstructor === "Array<any>" ||
            localPrimitiveOrConstructor === "any[]"
        ) {
            return Array.isArray(value) && value.every(v => v !== undefined);
        } else if (
            localPrimitiveOrConstructor === "Array<string>" ||
            localPrimitiveOrConstructor === "string[]"
        ) {
            return (
                Array.isArray(value) && value.every(v => typeof v === "string")
            );
        } else if (
            localPrimitiveOrConstructor === "Array<number>" ||
            localPrimitiveOrConstructor === "number[]"
        ) {
            return (
                Array.isArray(value) && value.every(v => typeof v === "number")
            );
        } else if (
            localPrimitiveOrConstructor === "Array<bigint>" ||
            localPrimitiveOrConstructor === "bigint[]"
        ) {
            return (
                Array.isArray(value) && value.every(v => typeof v === "bigint")
            );
        } else if (
            localPrimitiveOrConstructor === "Array<boolean>" ||
            localPrimitiveOrConstructor === "boolean[]"
        ) {
            return (
                Array.isArray(value) && value.every(v => typeof v === "boolean")
            );
        } else if (
            localPrimitiveOrConstructor === "Array<object>" ||
            localPrimitiveOrConstructor === "object[]"
        ) {
            return (
                Array.isArray(value) && value.every(v => typeof v === "object")
            );
        } else if (
            localPrimitiveOrConstructor === "Array<NonNullable<object>>" ||
            localPrimitiveOrConstructor === "NonNullable<object>[]"
        ) {
            return (
                Array.isArray(value) && value.every(v => isNonNullableObject(v))
            );
        }
        return typeof value === localPrimitiveOrConstructor;
    }
    return value instanceof localPrimitiveOrConstructor;
}

//helper for APIFilter
type KeyKeyFilterTypeMap<Types> = {
    [key in keyof Types]: { [key2 in key]: APIFilterValue<Types[key]> };
};
/**
 * type K = 'deleted' | 'active';
 * type F = APIFilterType<K, boolean>;
 *      // type F = {deleted: APIFilterValue<boolean>} | {active: APIFilterValue<boolean>}
 *
 */
//export type APIFilterType<Keys extends string, Type> = KeyKeyFilterTypeMapRecord<Keys, Type>[keyof KeyKeyFilterTypeMapRecord<Keys, Type>];
export type APIFilterRecord<Keys extends string, Type> = APIFilter<{
    [key in Keys]: Type;
}>;
/**
 * type T = {'name': string, 'active': boolean };
 * type F = APIFilter<T>;
 *      // type F = {name: APIFilterValue<string>} | {active: APIFilterValue<boolean>}
 *
 */
export type APIFilter<Types extends APIFilterDef<any>> =
    KeyKeyFilterTypeMap<Types>[keyof KeyKeyFilterTypeMap<Types>];
export type APIFilterDef<T> = Record<string, T>;
export type UnknownAPIFilter = APIFilter<APIFilterDef<unknown>>;
export type AnyAPIFilter = APIFilter<APIFilterDef<any>>;
/*
type sub<T> = T extends string ? string extends T ? never : T : never;
type f<K extends string> = {[k in K as K extends k ? never : K]: k};
type z = keyof f<'here' | 'there'>;
type s = {[k in f<'here' | 'there'> as k extends string ? 'no' : 'yes']: true };
type t = APIFilterDef<string>;
*/
// 'field1' | 'field2' | 'field3' | ...
export type APIFilterFields<T extends APIFilter<APIFilterDef<unknown>>> =
    keyof {
        [key in T as keyof key]: unknown;
    };
export const APIFilterValueOpsArray = [
    "gte",
    "lte",
    "gt",
    "lt",
    "eq",
    "ne",
    "null"
] as const;
export type APIFilterValueOps = (typeof APIFilterValueOpsArray)[number];
type APIFilterValueHelper<
    Type,
    Keys extends APIFilterValueOps = APIFilterValueOps
> = {
    [key in Keys]: { [key2 in key]: key2 extends "null" ? boolean : Type };
};
//{'gte': Type} | {'lte': Type} | {'gt': Type} | {'lt': Type} | {'eq': Type} | {'ne': Type} | {'null': boolean};
export type APIFilterValue<Type> =
    APIFilterValueHelper<Type>[keyof APIFilterValueHelper<Type>];
type AFVT<T, TT> = T extends "null" ? boolean : TT;
export function createAPIFilterValue<
    T,
    O extends APIFilterValueOps = APIFilterValueOps
>(op: O, value: AFVT<O, T>): APIFilterValue<T> {
    if (op === "null") {
        return { null: !!value };
    }
    return { [op]: value } as Exclude<APIFilterValue<T>, { null: boolean }>;
}
export function isAPIFilterEQ<T extends PrimitiveOrConstructor>(
    value: APIFilterValue<unknown>,
    type: T
): value is { eq: GuardedType<T> } {
    if (typeof value !== "object" || value === null) {
        return false;
    }
    return "eq" in value && genericTypeGuard<T>(value.eq, type);
}
type APIFilterTypeHelper<T extends APIFilterValue<any>> = { type: unknown } & {
    [Filter in T as Filter extends { eq: any }
        ? "type"
        : "other"]: Filter[keyof Filter];
};
/**
 * Gives the type of an APIFilter
 *
 * type T = APIFilterType<APIFilterValue<boolean>>;
 *      // type T = boolean
 */
export type APIFilterType<T extends APIFilterValue<any>> =
    APIFilterTypeHelper<T>["type"];
/*
type APIFilterNames<T extends APIFilter<any>> = keyof {[Filter in T as keyof Filter]: keyof Filter};
type FilterDesc<T extends APIFilter<any>> = {name: APIFilterNames<T>, values: T};
type ex = {[Filter in AccountFilter as keyof Filter]: {name: keyof Filter, type: Filter[keyof Filter] extends APIFilterValue<any> ? APIFilterType<Filter[keyof Filter]> : any}};
const c: ex = 'activated';
*/

export type APIOrder = "asc" | "desc";
export interface APIOrderField<Fields> {
    field: Fields;
    order: APIOrder;
}

export type SetElement<T> =
    T extends Set<infer U> ? U : T extends ReadonlySet<infer U> ? U : never;
